import { useId } from 'react'
import type { MouseHandlerDataParam } from 'recharts'
import {
	Area,
	CartesianGrid,
	ComposedChart,
	Legend,
	Line,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	XAxis,
	YAxis,
} from 'recharts'
import { Text } from '../Text'
import { getTrendYAxisProps, TREND_CURSOR, TrendChartTooltip, trendLegendFormatter, useTrendTooltipPos } from './shared'

export interface ParticipantTrendPoint {
	label: string
	overallRating: number | null
	avgParticipantRating: number | null
}

const SERIES_OVERALL_COLOR = '#3B82F6'
const SERIES_PARTICIPANT_COLOR = '#10B981'
const SERIES_ORDER = ['Overall Rating', 'Average Participant Rating']

export function PerformanceTrendChart({
	trend,
	scaleMax = 10,
}: Readonly<{ trend: ParticipantTrendPoint[]; scaleMax?: number }>) {
	const { tooltipPos, chartWrapperRef, onMouseMove, onMouseLeave: onChartMouseLeave } = useTrendTooltipPos()
	const overallGradientId = useId()
	const participantGradientId = useId()

	return (
		<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-4 text-left'>
			<Text variant='medium' weight='semibold'>
				Performance Trend
			</Text>
			{trend.length === 0 ? (
				<div className='flex h-48 items-center justify-center text-neutral-400 text-sm'>
					Not enough data to display a trend
				</div>
			) : (
				<div ref={chartWrapperRef}>
					<ResponsiveContainer width='100%' height={280}>
						<ComposedChart
							data={trend}
							margin={{ top: 10, right: 24, left: 0, bottom: 10 }}
							onMouseMove={onMouseMove as (state: MouseHandlerDataParam) => void}
							onMouseLeave={onChartMouseLeave}>
							<defs>
								<linearGradient id={overallGradientId} x1='0' y1='0' x2='0' y2='1'>
									<stop offset='5%' stopColor={SERIES_OVERALL_COLOR} stopOpacity={0.18} />
									<stop offset='95%' stopColor={SERIES_OVERALL_COLOR} stopOpacity={0} />
								</linearGradient>
								<linearGradient id={participantGradientId} x1='0' y1='0' x2='0' y2='1'>
									<stop offset='5%' stopColor={SERIES_PARTICIPANT_COLOR} stopOpacity={0.18} />
									<stop offset='95%' stopColor={SERIES_PARTICIPANT_COLOR} stopOpacity={0} />
								</linearGradient>
							</defs>
							<CartesianGrid strokeDasharray='3 3' stroke='#F3F4F6' vertical={false} />
							<XAxis dataKey='label' tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
							<YAxis {...getTrendYAxisProps(scaleMax)} />
							<RechartsTooltip
								content={<TrendChartTooltip seriesOrder={SERIES_ORDER} />}
								position={tooltipPos}
								cursor={TREND_CURSOR}
							/>
							<Area
								type='monotone'
								dataKey='overallRating'
								stroke='none'
								fill={`url(#${overallGradientId})`}
								name='Overall Rating'
								legendType='none'
							/>
							<Area
								type='monotone'
								dataKey='avgParticipantRating'
								stroke='none'
								fill={`url(#${participantGradientId})`}
								name='Average Participant Rating'
								legendType='none'
							/>
							<Line
								type='monotone'
								dataKey='overallRating'
								stroke={SERIES_OVERALL_COLOR}
								strokeWidth={2.5}
								dot={{ r: 4, fill: SERIES_OVERALL_COLOR, strokeWidth: 0 }}
								activeDot={{ r: 5 }}
								name='Overall Rating'
								connectNulls
							/>
							<Line
								type='monotone'
								dataKey='avgParticipantRating'
								stroke={SERIES_PARTICIPANT_COLOR}
								strokeWidth={2.5}
								dot={{ r: 4, fill: SERIES_PARTICIPANT_COLOR, strokeWidth: 0 }}
								activeDot={{ r: 5 }}
								name='Average Participant Rating'
								connectNulls
							/>
							<Legend
								iconType='plainline'
								iconSize={16}
								align='center'
								wrapperStyle={{ paddingTop: 16, fontSize: 12, color: '#6B7280' }}
								formatter={trendLegendFormatter}
							/>
						</ComposedChart>
					</ResponsiveContainer>
				</div>
			)}
		</div>
	)
}
